import {
  Box,
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import type { DocumentLink } from 'BrokerTsLib/src/dtos'
import type { ContractDocType } from 'BrokerTsLib/src/enums'
import { FormProvider, useForm } from 'BrokerTsLib/src/rhf'
import { useMemo } from 'react'

import {
  useGetDocumentTypes,
  useUploadInsuranceOfferDocument,
} from '../../hooks/useWizard'
import { Upload } from '../../icons'
import { DocumentsSectionHeader } from './DocumentsSectionHeader'

export const InsuranceUpload = ({
  offerId,
  existingDocuments,
  onUpload,
}: {
  offerId: number
  existingDocuments: DocumentLink[]
  onUpload?: () => void
}) => {
  const toast = useToast()
  const methods = useForm<{
    documentType: ContractDocType
    file: FileList
    overwrite: boolean
  }>()
  const { reset, handleSubmit, register, getValues, watch, setValue } = methods
  const { isOpen, onOpen, onClose } = useDisclosure()

  const { data: documentTypesData } = useGetDocumentTypes(offerId)
  const { mutateAsync: uploadAsync, isPending } = useUploadInsuranceOfferDocument()

  const documentTypes: [ContractDocType, string][] = useMemo(() => {
    const documentTypesArr = Array.isArray(documentTypesData?.data)
      ? documentTypesData?.data
      : []
    return documentTypesArr
      .slice().sort((a, b) => a.type - b.type)
      .map((type) => [
        type.type,
        existingDocuments.find((x) => x.type === type.type)?.description
        ?? type.description,
      ])
  }, [documentTypesData, existingDocuments])

  const documentType = watch('documentType')
  const overwrite = watch('overwrite')

  const upload = async (values: {
    documentType: ContractDocType
    file: FileList
    overwrite: boolean
  }) => {
    const data = new FormData()
    data.append('file', values.file.item(0) as File)

    await uploadAsync({ data, documentType, offerId, overwrite })
      .then((result) => {
        if (result.status == 200) {
          toast({
            title: 'Dokument byl úspěšně nahrán',
            status: 'success',
          })

          onUpload?.()
        }
      })
      .catch((e) =>
        toast({
          title: 'Nahrání dokumentu se nezdařilo',
          status: 'error',
        }),
      )

    reset()
  }

  const selectFile = () => {
    setValue('overwrite', false)

    if (documentType && existingDocuments.some((x) => x.type === documentType))
      onOpen()
    else document.getElementById('fileInput')?.click()
  }

  return (
    documentTypes.length > 0 && (
      <Box sx={{ display: 'grid', gap: 4, placeItems: 'start' }}>
        <DocumentsSectionHeader title="Nahrání dokumentů" />

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(upload)}>
            <Flex w="full" flexDirection="row" gap={2}>
              <Select {...register('documentType', { valueAsNumber: true })}>
                <option value="">Vyberte typ dokumentu</option>
                {documentTypes.map(([value, description]) => (
                  <option key={value} value={value}>
                    {description}
                  </option>
                ))}
              </Select>
              <Input
                id="fileInput"
                display="none"
                type="file"
                {...register('file', { onChange: () => upload(getValues()) })}
                multiple={false}
                accept=".pdf"
              />
              <Button
                colorScheme="primary"
                variant="outline"
                type="button"
                onClick={selectFile}
                isDisabled={!documentType}
                isLoading={isPending}
              >
                <Flex gap={2} pl={4} pr={6} alignItems="center">
                  <Upload color="primary.500" />
                  Nahrát
                </Flex>
              </Button>
            </Flex>
          </form>
        </FormProvider>

        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Dokument již existuje</ModalHeader>
            <ModalCloseButton />
            <ModalBody>Má být dokument přepsán?</ModalBody>

            <ModalFooter gap={2}>
              <Button
                colorScheme="primary"
                onClick={() => {
                  onClose()
                  setValue('overwrite', true)
                  document.getElementById('fileInput')?.click()
                }}
              >
                Přepsat
              </Button>
              <Button variant="ghost" onClick={onClose}>
                Zrušit
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    )
  )
}
